/**
 * SuperJarvis API Service - The Bridge to the Brain
 *
 * This service connects the User Tier (Firebase) to the Intelligence Tier (GCP).
 * All API calls include the Firebase ID token for authentication.
 *
 * MULTIMODAL SUPPORT:
 * - Text + Image via FLASH tier (low latency)
 * - Wispr (Ghost Mode) can send screenshots immediately
 */
import { getIdToken } from './firebase';
import type { ExecutionEvidence, ViewRecipe, WorkspaceEnvelope } from '../components/dynamic-view/types';

// Production-safe logging: verbose logs only when VITE_DEBUG is set
const DEBUG = import.meta.env.VITE_DEBUG === 'true';
const debugLog = DEBUG ? console.log.bind(console) : (() => {}) as (...args: unknown[]) => void;

// GCP Backend URLs
const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const AI_SERVICE_URL = import.meta.env.VITE_AI_SERVICE_URL || 'https://ai-service-buzbhnk2ha-uc.a.run.app';
// Genesis routing absorbed into ai-service (2026-02-16) — no separate GENESIS_URL needed
// Note: websocket-service deprecated 2026-02-12, using orbit-gateway for WebSocket
const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL || 'wss://orbit-gateway-buzbhnk2ha-uc.a.run.app/ws';
const WEBSOCKET_FALLBACK_URL = 'wss://orbit-gateway-buzbhnk2ha-uc.a.run.app/ws';

// =============================================================================
// TYPES
// =============================================================================

// Execution modes for the 3-lane polymorphic execution system
export type ExecutionMode = 'fast' | 'super';

export interface MessageContext {
  image?: string;        // Base64 encoded image (data:image/png;base64,... or raw)
  activeWindow?: string; // Title of active window (Ghost Mode)
  mode?: 'chat' | 'research' | 'create' | 'calendar';
  tier?: 'flash' | 'pro' | 'thinking';
  // Execution mode for 3-lane broadcast system:
  // - 'fast': Search lane only (quick answers)
  // - 'super': All 3 lanes (JSON, Search, External Discovery) → Synapse
  executionMode?: ExecutionMode;
  // Project ID for project-scoped conversations
  projectId?: string;
  projectName?: string;
  projectKnowledge?: string[];
}

export interface IntentRequest {
  intent: string;
  context?: Record<string, unknown>;
  mode?: 'chat' | 'research' | 'create' | 'calendar';
  image?: string;
}

export interface IntentResponse {
  success: boolean;
  result: unknown;
  agent: string;
  confidence: number;
  correlation_id?: string;
  model?: string;
  tier?: string;
  vision_used?: boolean;
  sources?: string[];
  lane_contributions?: Record<string, number>;
  // Dynamic View v2: structured ViewRecipe from SUPER mode
  view_recipe?: ViewRecipe;
  envelopes?: WorkspaceEnvelope[];
  execution_evidence?: ExecutionEvidence;
  pay_request?: any;
  verification_log?: {
    winning_lane: string;
    winning_source: string;
    confidence: number;
  };
  // Action-Response Contract: structured action results from genesis
  executed?: boolean;        // Was an action actually executed?
  action?: string;           // What action? (task_create, email_send, calendar_create, etc.)
  task_result?: {            // Task-specific result data
    task_id?: string;
    title?: string;
  };
  intent?: string;           // Classified intent domain
  fallback?: boolean;        // Did the action fall back to AI-only response?
  // Clarification response (for goal intents)
  clarification?: {
    questions_asked?: string[];
    stale_context_referenced?: boolean;
    awaiting_input?: boolean;
    suggested_actions?: string[];
    proactive_actions?: Array<{
      label: string;
      action: string;
      navigation?: { path: string; prefill?: boolean; type?: string };
    }>;
  };
}

/** A2A StatusEvent from an agent lane or synapse */
export interface A2AStatusEvent {
  agent_id: string;
  status: 'processing' | 'complete' | 'failed';
  message: string;
  estimated_ms_remaining: number;
}

export interface StreamChunk {
  type: 'content' | 'agent' | 'status' | 'progress' | 'complete' | 'error' | 'lane_start' | 'lane_progress' | 'lane_complete' | 'synthesis_start' | 'synthesis_complete' | 'dynamic_view_ready' | 'synthesizing';
  data: string;
  agent?: string;
  model?: string;
  tier?: string;
  timestamp: string;
  // Lane/synthesis fields (from manus-service native SSE)
  lane?: string;
  confidence?: number;
  success?: boolean;
  execution_time_ms?: number;
  has_data?: boolean;
  view_recipe?: Record<string, unknown>;
  envelopes?: WorkspaceEnvelope[];
  execution_evidence?: ExecutionEvidence;
  // A2A StatusEvent fields (when type === 'status')
  statusEvent?: A2AStatusEvent;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

type TraceableStreamChunk = Pick<StreamChunk, 'type'> & {
  data?: unknown;
  timestamp?: string;
};

const TRACE_BREADCRUMB_TYPES = new Set<StreamChunk['type']>([
  'status',
  'lane_start',
  'lane_progress',
  'lane_complete',
  'synthesis_start',
  'synthesis_complete',
  'complete',
  'dynamic_view_ready',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseTraceData(value: unknown): Record<string, unknown> {
  if (isRecord(value)) return value;
  if (typeof value !== 'string') return {};

  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function copyTraceField(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  field: string
) {
  if (source[field] !== undefined && source[field] !== null) {
    target[field] = source[field];
  }
}

/**
 * Build a production-safe trace breadcrumb for SSE lifecycle events.
 *
 * This is deterministic Stage telemetry only: it keeps correlation/evidence
 * metadata lossless enough for Cloud Log tracing while deliberately excluding
 * prompt text, result text, and raw lane payloads.
 */
export function buildStreamTraceBreadcrumb(chunk: TraceableStreamChunk): Record<string, unknown> | null {
  if (!TRACE_BREADCRUMB_TYPES.has(chunk.type)) return null;

  const data = parseTraceData(chunk.data);
  const evidence = parseTraceData(data.execution_evidence);
  const trace: Record<string, unknown> = { type: chunk.type };

  [
    'correlation_id',
    'lane',
    'success',
    'execution_time_ms',
    'mode',
    'confidence',
    'lanes_succeeded',
    'lanes_failed',
    'has_dynamic_view',
    'agent_id',
    'status',
    'estimated_ms_remaining',
  ].forEach((field) => copyTraceField(data, trace, field));

  if (Array.isArray(data.sources)) {
    trace.sources_count = data.sources.length;
  }

  copyTraceField(evidence, trace, 'truth_state');
  copyTraceField(evidence, trace, 'terminal_actions_allowed');
  copyTraceField(evidence, trace, 'correlation_id');

  if (Array.isArray(evidence.provider_request_ids)) {
    trace.provider_request_count = evidence.provider_request_ids.length;
  }
  if (Array.isArray(evidence.states)) {
    trace.evidence_state_count = evidence.states.length;
  }

  return Object.keys(trace).length > 1 ? trace : null;
}

function emitStreamTraceBreadcrumb(chunk: TraceableStreamChunk) {
  const trace = buildStreamTraceBreadcrumb(chunk);
  if (!trace) return;

  console.info('[SuperJarvisTrace]', JSON.stringify(trace));
}

export type WebSocketStatus = 'connected' | 'connecting' | 'disconnected';

export interface WebSocketMessage {
  type: 'message' | 'typing' | 'status';
  data?: unknown;
  content?: string;
  userId?: string;
}

export interface WebSocketEventHandlers {
  onOpen?: () => void;
  onMessage?: (message: WebSocketMessage) => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  onStatusChange?: (status: WebSocketStatus) => void;
  onTyping?: (userId: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get authenticated headers with Firebase token
 */
async function getHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Convert file to Base64 data URL
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Compress image if needed (for large screenshots)
 */
export async function compressImage(dataUrl: string, maxWidth = 1920): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // If already small enough, return as-is
      if (img.width <= maxWidth) {
        resolve(dataUrl);
        return;
      }

      // Resize
      const canvas = document.createElement('canvas');
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } else {
        resolve(dataUrl);
      }
    };
    img.src = dataUrl;
  });
}

// =============================================================================
// WEBSOCKET CONNECTION MANAGER
// =============================================================================

/**
 * WebSocket Connection Manager
 *
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Message queue for offline scenarios
 * - Connection status tracking
 * - Event-driven architecture
 */
export class WebSocketManager {
  private ws: WebSocket | null = null;
  private url: string;
  private fallbackUrl: string;
  private handlers: WebSocketEventHandlers = {};
  private status: WebSocketStatus = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000; // 30 seconds
  private reconnectTimeout: number | null = null;
  private messageQueue: WebSocketMessage[] = [];
  private isIntentionalClose = false;

  constructor(url: string = WEBSOCKET_URL, fallbackUrl: string = WEBSOCKET_FALLBACK_URL) {
    this.url = url;
    this.fallbackUrl = fallbackUrl;
  }

  /**
   * Connect to WebSocket server
   */
  async connect(handlers: WebSocketEventHandlers = {}): Promise<void> {
    this.handlers = handlers;
    this.isIntentionalClose = false;

    // Try primary URL first, then fallback
    try {
      await this.attemptConnection(this.url);
    } catch (error) {
      console.warn('[WebSocket] Primary URL failed, trying fallback...', error);
      try {
        await this.attemptConnection(this.fallbackUrl);
      } catch (fallbackError) {
        console.error('[WebSocket] Both URLs failed:', fallbackError);
        this.updateStatus('disconnected');
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Attempt to establish WebSocket connection
   */
  private async attemptConnection(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.updateStatus('connecting');

        // Get auth token - pass via Sec-WebSocket-Protocol header for security
        // (tokens in URLs can be logged and cached)
        getIdToken().then(token => {
          // Use subprotocol to pass auth token securely (not in URL)
          const protocols = token ? [`auth-${token}`] : undefined;
          const wsUrl = url;
          this.ws = new WebSocket(wsUrl, protocols);

          this.ws.onopen = () => {
            debugLog('[WebSocket] Connected to', url);
            this.reconnectAttempts = 0;
            this.updateStatus('connected');
            this.flushMessageQueue();
            this.handlers.onOpen?.();
            resolve();
          };

          this.ws.onmessage = (event) => {
            try {
              const message: WebSocketMessage = JSON.parse(event.data);

              // Handle typing events
              if (message.type === 'typing' && message.userId) {
                this.handlers.onTyping?.(message.userId);
              }

              this.handlers.onMessage?.(message);
            } catch (error) {
              console.error('[WebSocket] Message parse error:', error);
            }
          };

          this.ws.onclose = () => {
            debugLog('[WebSocket] Connection closed');
            this.updateStatus('disconnected');
            this.handlers.onClose?.();

            // Auto-reconnect unless intentionally closed
            if (!this.isIntentionalClose) {
              this.scheduleReconnect();
            }
          };

          this.ws.onerror = (error) => {
            console.error('[WebSocket] Error:', error);
            this.handlers.onError?.(error);
            reject(error);
          };
        }).catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Schedule reconnection with exponential backoff
   * Delays: 1s, 2s, 4s, 8s, 16s, 30s (max)
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    debugLog(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(this.handlers);
    }, delay);
  }

  /**
   * Send message via WebSocket
   * Queues message if not connected
   */
  send(message: WebSocketMessage): void {
    if (this.status === 'connected' && this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
      } catch (error) {
        console.error('[WebSocket] Send error:', error);
        this.queueMessage(message);
      }
    } else {
      this.queueMessage(message);
    }
  }

  /**
   * Queue message for later delivery
   */
  private queueMessage(message: WebSocketMessage): void {
    this.messageQueue.push(message);
    debugLog(`[WebSocket] Message queued (${this.messageQueue.length} in queue)`);
  }

  /**
   * Flush queued messages when connection restored
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    debugLog(`[WebSocket] Flushing ${this.messageQueue.length} queued messages`);

    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  /**
   * Update connection status
   */
  private updateStatus(status: WebSocketStatus): void {
    this.status = status;
    this.handlers.onStatusChange?.(status);
  }

  /**
   * Get current connection status
   */
  getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.status === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Close connection
   */
  disconnect(): void {
    this.isIntentionalClose = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.updateStatus('disconnected');
  }

  /**
   * Send typing indicator
   */
  sendTyping(userId: string): void {
    this.send({
      type: 'typing',
      userId
    });
  }
}

// =============================================================================
// MULTIMODAL CHAT API
// =============================================================================

/** Default stream inactivity timeout (ms) — if no SSE chunk arrives in this window, abort.
 *  Set to 120s because SUPER-mode flight queries can take 80-90s with lane
 *  execution + synthesis, and progress events may have gaps of ~15-20s
 *  between the last timed message and the actual result arriving.  */
const STREAM_INACTIVITY_TIMEOUT_MS = 120_000;

/** Max retries for transient stream failures */
const MAX_STREAM_RETRIES = 2;

/**
 * Send a message with SSE streaming response
 *
 * Supports multimodal (text + image) with real-time streaming.
 * Uses AbortController with a 45s inactivity timeout that resets on every chunk.
 * Callers can pass an external AbortSignal to cancel from outside.
 */
export async function sendMessageStream(
  text: string,
  context: MessageContext | undefined,
  history: ChatMessage[] | undefined,
  onChunk: (chunk: StreamChunk) => void,
  onComplete: (result: IntentResponse) => void,
  onError: (error: Error) => void,
  options?: { signal?: AbortSignal; timeoutMs?: number }
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? STREAM_INACTIVITY_TIMEOUT_MS;

  // AbortController: cancel on external signal OR inactivity timeout
  const controller = new AbortController();

  try {
    const headers = await getHeaders();
    debugLog('[API] sendMessageStream - Auth header present:', !!headers['Authorization']);

    // Get user's timezone from browser
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const payload: Record<string, unknown> = {
      utterance: text,
      history: history || [],
      mode: context?.mode || 'chat',
      timezone: userTimezone  // Pass user's local timezone
    };

    // Execution mode: always async (AI decides, no fast/super split)
    payload.execution_mode = 'super';
    // Signal manus-service to return SSE stream (not blocking JSON)
    payload.stream = true;

    // Add project context for project-scoped conversations
    if (context?.projectId) {
      payload.project_id = context.projectId;
      if (context.projectName) payload.project_name = context.projectName;
      if (context.projectKnowledge?.length) payload.project_knowledge = context.projectKnowledge;
    }

    // Add image if present (multimodal)
    if (context?.image) {
      payload.image = context.image;
    }

    // Add active window context (Ghost Mode)
    if (context?.activeWindow) {
      payload.context = {
        ...(payload.context as object || {}),
        activeWindow: context.activeWindow
      };
    }

    debugLog('[API] Sending to:', `${ORBIT_GATEWAY_URL}/intent/stream`);
    debugLog('[API] Payload (truncated):', { ...payload, image: payload.image ? '[IMAGE]' : undefined });
    if (options?.signal) {
      options.signal.addEventListener('abort', () => controller.abort());
    }

    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    const resetInactivityTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        console.warn(`[API] Stream inactivity timeout (${timeoutMs}ms) — aborting`);
        controller.abort();
      }, timeoutMs);
    };
    resetInactivityTimer();

    const response = await fetch(`${ORBIT_GATEWAY_URL}/intent/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    debugLog('[API] Response status:', response.status, response.statusText);

    if (!response.ok) {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      const errorBody = await response.text();
      console.error('[API] Error response body:', errorBody);
      throw new Error(`API error: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    if (!response.body) {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let chunkCount = 0;
    let receivedTerminalChunk = false;

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          debugLog('[API] Stream complete. Total chunks received:', chunkCount);
          if (!receivedTerminalChunk) {
            throw new Error("Stream disconnected unexpectedly (timeout or network error)");
          }
          break;
        }

        // Reset inactivity timer on every received chunk
        resetInactivityTimer();

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            chunkCount++;

            if (data === '[DONE]') {
              debugLog('[API] Received [DONE] signal');
              continue;
            }

            try {
              const chunk = JSON.parse(data) as StreamChunk;
              emitStreamTraceBreadcrumb(chunk);
              debugLog('[API] Chunk received:', chunk.type, chunk.type === 'content' ? `(${chunk.data?.length || 0} chars)` : '');

              if (chunk.type === 'complete') {
                receivedTerminalChunk = true;
                // Defensive parsing: if chunk.data is somehow a string, JSON.parse it
                const result = (typeof chunk.data === 'string'
                  ? JSON.parse(chunk.data)
                  : chunk.data) as unknown as IntentResponse;
                debugLog('[API] Complete response - view_recipe:',
                  result.view_recipe ? `type=${result.view_recipe.type}, html_len=${(result.view_recipe as any)?.html?.length || 0}` : 'none');
                debugLog('[API] Complete response - sources:', result.sources?.length || 0);
                onComplete(result);
              } else if (chunk.type === 'status') {
                // A2A StatusEvent — attach parsed event and forward to UI
                const statusData = typeof chunk.data === 'string' ? JSON.parse(chunk.data) : chunk.data;
                debugLog('[API] A2A StatusEvent:', statusData?.agent_id, statusData?.status);
                const enriched: StreamChunk = {
                  ...chunk,
                  statusEvent: statusData as A2AStatusEvent,
                };
                onChunk(enriched);
              } else if (chunk.type === 'dynamic_view_ready') {
                // DV arrives async after complete — pass through to UI with view_recipe
                const dvData = typeof chunk.data === 'string' ? JSON.parse(chunk.data) : chunk.data;
                debugLog('[API] Dynamic View ready (async):', dvData ? 'has view_recipe' : 'empty');
                const enriched: StreamChunk = {
                  ...chunk,
                  view_recipe: dvData as Record<string, unknown>,
                };
                onChunk(enriched);
              } else if (chunk.type === 'error') {
                receivedTerminalChunk = true;
                console.error('[API] Error chunk:', chunk.data);
                onError(new Error(chunk.data));
              } else {
                // lane_start, lane_complete, synthesis_start, content, etc.
                onChunk(chunk);
              }
            } catch {
              // Handle plain text chunks — but never render internal event JSON
              // Guard: if the raw text looks like a manus SSE event envelope, skip it
              if (data.includes('"type"') && (
                data.includes('"complete"') || data.includes('"error"') ||
                data.includes('"lane_start"') || data.includes('"lane_complete"') ||
                data.includes('"synthesis_start"') || data.includes('"dynamic_view_ready"')
              )) {
                console.warn('[API] Skipping unparseable internal event:', data.substring(0, 120));
              } else {
                debugLog('[API] Plain text chunk:', data.substring(0, 100));
                onChunk({
                  type: 'content',
                  data,
                  timestamp: new Date().toISOString()
                });
              }
            }
          }
        }
      }
    } finally {
      if (inactivityTimer) clearTimeout(inactivityTimer);
    }
  } catch (error) {
    // AbortController can surface as DOMException(AbortError) *or* as a
    // TypeError("network error" / "Failed to fetch") depending on the
    // browser and whether the abort hit during the fetch or during
    // reader.read().  Detect both so the user gets a helpful message
    // instead of the opaque "Error: network error".
    const isAbort =
      (error instanceof DOMException && error.name === 'AbortError') ||
      (controller.signal.aborted);
    if (isAbort) {
      console.warn('[API] Stream aborted (inactivity timeout or user cancel)');
      const abortErr = new Error('This is taking longer than expected. SuperJarvis is still working — please wait or try again.');
      (abortErr as any).isTimeout = true;
      onError(abortErr);
      return;
    }
    console.error('[API] Stream error:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Send a message with automatic retry on transient failures.
 * Retries up to MAX_STREAM_RETRIES times with exponential backoff.
 *
 * sendMessageStream swallows most errors via the onError callback (it does
 * not re-throw), so we wrap onError to detect retryable failures and
 * propagate them into the retry loop.
 */
export async function sendMessageStreamWithRetry(
  text: string,
  context: MessageContext | undefined,
  history: ChatMessage[] | undefined,
  onChunk: (chunk: StreamChunk) => void,
  onComplete: (result: IntentResponse) => void,
  onError: (error: Error) => void,
  options?: { signal?: AbortSignal; timeoutMs?: number }
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_STREAM_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoffMs = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
      debugLog(`[API] Retry attempt ${attempt}/${MAX_STREAM_RETRIES} after ${backoffMs}ms`);
      await new Promise(resolve => setTimeout(resolve, backoffMs));
    }

    // Wrap onError so we can detect retryable failures within the callback
    let callbackError: Error | null = null;
    let completed = false;
    const wrappedOnError = (err: Error) => { callbackError = err; };
    const wrappedOnComplete = (result: IntentResponse) => { completed = true; onComplete(result); };

    try {
      await sendMessageStream(text, context, history, onChunk, wrappedOnComplete, wrappedOnError, options);
    } catch (err) {
      callbackError = err instanceof Error ? err : new Error(String(err));
    }

    // If the stream completed successfully, we're done
    if (completed) return;

    // If an error occurred, decide whether to retry
    if (callbackError) {
      lastError = callbackError;
      const isTimeout = (callbackError as any).isTimeout === true;
      const isNetworkErr = callbackError.message.includes('network error') ||
                           callbackError.message.includes('Failed to fetch') ||
                           callbackError.message.includes('disconnected unexpectedly');

      // Don't retry: user-initiated abort or client errors (4xx)
      if (options?.signal?.aborted) break;
      if (lastError.message.includes('API error: 4')) break;

      // Retry on timeout or network errors
      if ((isTimeout || isNetworkErr) && attempt < MAX_STREAM_RETRIES) {
        console.warn(`[API] Attempt ${attempt + 1} failed (retryable):`, lastError.message);
        continue;
      }

      // Non-retryable or exhausted retries — surface to caller
      break;
    }

    // No error and not completed — shouldn't happen, but exit cleanly
    return;
  }

  if (lastError) {
    onError(lastError);
  }
}

// =============================================================================
// UTILITY APIs
// =============================================================================

/**
 * Check service health
 */
export async function checkHealth(): Promise<Record<string, boolean>> {
  const services = {
    gateway: `${ORBIT_GATEWAY_URL}/health`,
    genesis: `${AI_SERVICE_URL}/health`,  // genesis module inside ai-service
    ai: `${AI_SERVICE_URL}/health`
  };

  const results: Record<string, boolean> = {};

  await Promise.all(
    Object.entries(services).map(async ([name, url]) => {
      try {
        const response = await fetch(url, { method: 'GET' });
        results[name] = response.ok;
      } catch {
        results[name] = false;
      }
    })
  );

  return results;
}


// =============================================================================
// TRUST-DRIP
// =============================================================================

/**
 * Exception types for Trust-Drip flow
 */
export class PermissionRequiredException extends Error {
  constructor(
    public provider: string,
    public requiredScopes: string[],
    message: string
  ) {
    super(message);
    this.name = 'PermissionRequiredException';
  }
}

/**
 * Check if a response requires permission
 */
export function checkPermissionRequired(response: IntentResponse): PermissionRequiredException | null {
  const result = response.result as Record<string, unknown>;

  if (result?.permission_required) {
    return new PermissionRequiredException(
      result.provider as string,
      result.scopes as string[],
      result.message as string
    );
  }

  return null;
}
